import clsx from "clsx";

export interface RewardSettingsValues {
    name: string;
    description: string;
    cost: number;
    rpAmount: number;
    backgroundColor: string;
    userInputRequired: boolean;
    skipQueue: boolean;
    globalCooldownEnabled: boolean;
    maxPerStreamEnabled: boolean;
    maxPerUserPerStreamEnabled: boolean;
    globalCooldownSeconds: number;
    limitPerStream: number;
    limitPerUserPerStream: number;
}

export const DEFAULT_STREAM_TEAM_REWARD: RewardSettingsValues = {
    name: "Vanderheim Challenge",
    description: "Redeem to trigger a stream team challenge event.",
    cost: 1000,
    rpAmount: 50,
    backgroundColor: "#9147FF",
    userInputRequired: false,
    skipQueue: false,
    globalCooldownEnabled: false,
    maxPerStreamEnabled: false,
    maxPerUserPerStreamEnabled: false,
    globalCooldownSeconds: 1800,
    limitPerStream: 25,
    limitPerUserPerStream: 1,
};

export const DEFAULT_ACADEMY_REWARD: RewardSettingsValues = {
    ...DEFAULT_STREAM_TEAM_REWARD,
    name: "VRX Academy Runestone",
    description: "Redeem to collect this Academy member's Runestone.",
};

const inputClass =
    "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100";

function Toggle({
    label,
    hint,
    value,
    onChange,
}: {
    label: string;
    hint: string;
    value: boolean;
    onChange: (next: boolean) => void;
}) {
    return (
        <div className="flex items-center justify-between gap-3">
            <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    {label}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                    {hint}
                </p>
            </div>
            <button
                type="button"
                role="switch"
                aria-checked={value}
                aria-label={label}
                onClick={() => onChange(!value)}
                className={clsx(
                    "relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors",
                    value ? "bg-brand" : "bg-slate-300 dark:bg-slate-600",
                )}
            >
                <span
                    className={clsx(
                        "inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                        value ? "translate-x-6" : "translate-x-1",
                    )}
                />
            </button>
        </div>
    );
}

interface RewardSettingsFormProps {
    values: RewardSettingsValues;
    onChange: (patch: Partial<RewardSettingsValues>) => void;
    namePlaceholder: string;
    descriptionPlaceholder: string;
}

export default function RewardSettingsForm({
    values,
    onChange,
    namePlaceholder,
    descriptionPlaceholder,
}: RewardSettingsFormProps) {
    const toInt = (raw: string, min: number, max?: number) => {
        const parsed = Math.max(min, Number.parseInt(raw || String(min), 10));
        return max === undefined ? parsed : Math.min(max, parsed);
    };

    return (
        <div className="space-y-3">
            <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Reward Name
                </label>
                <input
                    type="text"
                    maxLength={45}
                    value={values.name}
                    onChange={(e) => onChange({ name: e.target.value })}
                    className={inputClass}
                    placeholder={namePlaceholder}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Reward Description
                </label>
                <textarea
                    maxLength={200}
                    value={values.description}
                    onChange={(e) => onChange({ description: e.target.value })}
                    className={clsx(inputClass, "min-h-24")}
                    placeholder={descriptionPlaceholder}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Reward Cost
                </label>
                <input
                    type="number"
                    min={1}
                    value={values.cost}
                    onChange={(e) =>
                        onChange({ cost: toInt(e.target.value, 1) })
                    }
                    className={inputClass}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    RP Awarded Per Redemption
                </label>
                <input
                    type="number"
                    min={1}
                    value={values.rpAmount}
                    onChange={(e) =>
                        onChange({ rpAmount: toInt(e.target.value, 1) })
                    }
                    className={inputClass}
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Reward Background Color
                </label>
                <div className="flex items-center gap-2">
                    <input
                        type="color"
                        aria-label="Reward background color picker"
                        value={values.backgroundColor}
                        onChange={(e) =>
                            onChange({
                                backgroundColor: e.target.value.toUpperCase(),
                            })
                        }
                        className="h-10 w-14 rounded-md border border-slate-300 bg-white p-1 dark:border-slate-600 dark:bg-slate-950"
                    />
                    <input
                        type="text"
                        maxLength={7}
                        value={values.backgroundColor}
                        onChange={(e) =>
                            onChange({
                                backgroundColor: e.target.value.toUpperCase(),
                            })
                        }
                        className={inputClass}
                        placeholder="#9147FF"
                    />
                </div>
            </div>

            <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
                <Toggle
                    label="User Input Required"
                    hint="Require a typed prompt input for each redemption."
                    value={values.userInputRequired}
                    onChange={(userInputRequired) =>
                        onChange({ userInputRequired })
                    }
                />
            </div>

            <div className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
                <Toggle
                    label="Skip Reward Request Queue"
                    hint="Future redemptions skip manual review."
                    value={values.skipQueue}
                    onChange={(skipQueue) => onChange({ skipQueue })}
                />
            </div>

            <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
                <Toggle
                    label="Enable Global Cooldown"
                    hint="Control is_global_cooldown_enabled."
                    value={values.globalCooldownEnabled}
                    onChange={(globalCooldownEnabled) =>
                        onChange({ globalCooldownEnabled })
                    }
                />
                {values.globalCooldownEnabled && (
                    <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                            Global Cooldown (seconds)
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={604800}
                            value={values.globalCooldownSeconds}
                            onChange={(e) =>
                                onChange({
                                    globalCooldownSeconds: toInt(
                                        e.target.value,
                                        1,
                                        604800,
                                    ),
                                })
                            }
                            className={inputClass}
                        />
                    </div>
                )}

                <Toggle
                    label="Enable Max Per Stream"
                    hint="Control is_max_per_stream_enabled."
                    value={values.maxPerStreamEnabled}
                    onChange={(maxPerStreamEnabled) =>
                        onChange({ maxPerStreamEnabled })
                    }
                />
                {values.maxPerStreamEnabled && (
                    <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                            Limit Per Stream
                        </label>
                        <input
                            type="number"
                            min={1}
                            value={values.limitPerStream}
                            onChange={(e) =>
                                onChange({
                                    limitPerStream: toInt(e.target.value, 1),
                                })
                            }
                            className={inputClass}
                        />
                    </div>
                )}

                <Toggle
                    label="Enable Max Per User / Stream"
                    hint="Control is_max_per_user_per_stream_enabled."
                    value={values.maxPerUserPerStreamEnabled}
                    onChange={(maxPerUserPerStreamEnabled) =>
                        onChange({ maxPerUserPerStreamEnabled })
                    }
                />
                {values.maxPerUserPerStreamEnabled && (
                    <div>
                        <label className="block text-xs font-medium text-slate-700 dark:text-slate-300">
                            Limit Per User / Stream
                        </label>
                        <input
                            type="number"
                            min={1}
                            value={values.limitPerUserPerStream}
                            onChange={(e) =>
                                onChange({
                                    limitPerUserPerStream: toInt(
                                        e.target.value,
                                        1,
                                    ),
                                })
                            }
                            className={inputClass}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
